import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, Edit2, Trash2, Eye, Users, 
  Instagram, Linkedin, Twitter, Facebook, Youtube,
  MoreVertical, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  youtube: Youtube,
};

const platformColors = {
  instagram: 'bg-gradient-to-br from-purple-600 to-pink-500',
  linkedin: 'bg-[#0077b5]',
  twitter: 'bg-black',
  facebook: 'bg-[#1877f2]',
  youtube: 'bg-[#ff0000]',
};

const formatFollowers = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count;
};

const AdminSocialPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, page: null });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/social-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching social pages:', error);
      toast.error('Failed to load social pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.page) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/social-pages/${deleteModal.page.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Social page deleted');
      setPages(pages.filter(p => p.id !== deleteModal.page.id));
      setDeleteModal({ open: false, page: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (page) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/social-pages/${page.id}`, 
        { is_active: !page.is_active },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPages(pages.map(p => 
        p.id === page.id ? { ...p, is_active: !p.is_active } : p
      ));
      toast.success(`Page ${page.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          page.handle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = !filterPlatform || page.platform === filterPlatform;
    return matchesSearch && matchesPlatform;
  });

  const platforms = ['instagram', 'linkedin', 'twitter', 'facebook', 'youtube'];

  return (
    <div className="p-6" data-testid="admin-social-pages">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Social Pages</h1>
          <p className="text-sm text-gray-500">Manage meme pages, influencers, and publication social handles</p>
        </div>
        <Link
          to="/admin/social-pages/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] transition-colors"
          data-testid="add-social-page-btn"
        >
          <Plus className="w-4 h-4" />
          Add Social Page
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or handle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            data-testid="search-input"
          />
        </div>
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
          data-testid="platform-filter"
        >
          <option value="">All Platforms</option>
          {platforms.map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {platforms.map(platform => {
          const count = pages.filter(p => p.platform === platform).length;
          const Icon = platformIcons[platform];
          return (
            <div key={platform} className="bg-white border border-gray-100 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${platformColors[platform]} text-white`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a1a1a]">{count}</p>
                  <p className="text-xs text-gray-500 capitalize">{platform}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">No Social Pages</h3>
            <p className="text-gray-500 mb-4">Start by adding your first social page</p>
            <Link
              to="/admin/social-pages/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Add Social Page
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Page</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Platform</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Followers</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Packages</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPages.map(page => {
                const Icon = platformIcons[page.platform] || Users;
                return (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {page.logo ? (
                          <img src={page.logo} alt={page.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className={`w-10 h-10 rounded-full ${platformColors[page.platform]} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-[#1a1a1a]">{page.name}</p>
                          <p className="text-sm text-gray-500">@{page.handle}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white ${platformColors[page.platform]}`}>
                        <Icon className="w-3 h-3" />
                        {page.platform}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-[#1a1a1a]">{formatFollowers(page.followers)}</span>
                      {page.engagement_rate && (
                        <span className="text-xs text-gray-500 ml-2">({page.engagement_rate}% eng)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 capitalize">{page.page_type?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{page.packages?.length || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(page)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          page.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {page.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {page.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/social-pages/${page.id}/edit`}
                          className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteModal({ open: true, page })}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">Delete Social Page</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteModal.page?.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, page: null })}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSocialPages;
